import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ApiService } from '../services/api.service';
import { extractList } from '../shared/utils/extract-list';
import { AuthService } from '../core/services/auth.service';

interface LeaveRequest {
  id: string;
  employeeId?: string;
  employeeName: string;
  type: string;
  fromDate: string;
  toDate: string;
  reason: string;
  status: string;
  teamLeadForwardedAt?: string | null;
  teamLeadRemarks?: string | null;
  projectManagerRemarks?: string | null;
}

@Component({
  selector: 'app-leave-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './leave-management.component.html',
  styleUrl: './leave-management.component.scss'
})
export class LeaveManagementComponent implements OnInit {

  loading = false;
  searchText = '';
  selectedStatus = 'All';

  leaveRequests: LeaveRequest[] = [];
  filteredLeaves: LeaveRequest[] = [];
  statuses = [
    'All', 'Draft', 'Pending', 'Submitted', 'TeamLeadReview', 'ProjectManagerReview',
    'HrReview', 'CeoReview', 'Approved', 'Rejected', 'Cancelled'
  ];
  leaveTypes = ['Annual Leave', 'Sick Leave', 'Casual Leave', 'Maternity Leave', 'Paternity Leave'];
  balances: any[] = [];
  calendarDays: any[] = [];
  selectedEmployeeIdForBalance = '';

  displayedColumns: string[] = ['employee', 'type', 'dates', 'reason', 'status', 'actions'];

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    private auth: AuthService
  ) {}

  get canCreateLeave(): boolean {
    return this.auth.hasAnyRole(['employee', 'hr', 'team_lead', 'project_manager', 'super_admin', 'tenant_admin']);
  }

  get canForwardLeave(): boolean {
    return this.auth.hasAnyRole(['team_lead', 'project_manager', 'hr', 'super_admin', 'tenant_admin']);
  }

  get canDecideLeave(): boolean {
    return this.auth.hasAnyRole(['project_manager', 'hr', 'super_admin', 'tenant_admin']);
  }

  ngOnInit(): void {
    this.loadLeaveRequests();
  }

  loadLeaveRequests(): void {
    this.loading = true;

    this.apiService.getLeaveRequests().subscribe({
      next: (response) => {
        const data = extractList(response);
        this.leaveRequests = data.map((leave: any) => ({
          id: String(leave.id),
          employeeId: leave.employeeId ? String(leave.employeeId) : undefined,
          employeeName:
            leave.employeeName ||
            leave.employee?.displayName ||
            leave.employee?.name ||
            `Employee #${leave.employeeId || 'N/A'}`,
          type: leave.leaveType || leave.type || 'Casual Leave',
          fromDate: leave.startDate || leave.fromDate || '',
          toDate: leave.endDate || leave.toDate || '',
          reason: leave.reason || '',
          status: this.normalizeLeaveStatus(leave.statusLabel || leave.status),
          teamLeadForwardedAt: leave.teamLeadForwardedAt ?? null,
          teamLeadRemarks: leave.teamLeadRemarks ?? null,
          projectManagerRemarks: leave.projectManagerRemarks ?? null
        }));
        this.filteredLeaves = [...this.leaveRequests];
        this.loading = false;
        const firstEmp = this.leaveRequests.find(l => l.employeeId)?.employeeId;
        if (firstEmp) {
          this.selectedEmployeeIdForBalance = firstEmp;
          this.loadBalance(firstEmp);
          this.loadCalendar(firstEmp);
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadBalance(employeeId: string): void {
    this.apiService.getLeaveBalanceV1(employeeId).subscribe({
      next: (res) => { this.balances = res?.data ?? extractList(res) ?? []; },
      error: () => { this.balances = []; }
    });
  }

  loadCalendar(employeeId: string): void {
    const now = new Date();
    const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
    this.apiService.getLeaveCalendar(from, to, employeeId).subscribe({
      next: (res) => { this.calendarDays = res?.data ?? res ?? []; },
      error: () => { this.calendarDays = []; }
    });
  }

  filterLeaves(): void {
    const search = this.searchText.toLowerCase().trim();
    this.filteredLeaves = this.leaveRequests.filter(leave => {
      const matchesSearch = leave.employeeName.toLowerCase().includes(search) ||
        leave.type.toLowerCase().includes(search) ||
        leave.reason.toLowerCase().includes(search);
      const matchesStatus = this.selectedStatus === 'All' || leave.status === this.selectedStatus;
      return matchesSearch && matchesStatus;
    });
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedStatus = 'All';
    this.filteredLeaves = [...this.leaveRequests];
  }

  forwardLeave(leave: LeaveRequest): void {
    const remarks = prompt('Team lead remarks (forward to project manager):') || '';
    this.apiService.forwardLeaveRequest(leave.id, remarks).subscribe({
      next: () => this.loadLeaveRequests(),
      error: (error) => console.error('Forward Leave Error:', error)
    });
  }

  approveLeave(leave: LeaveRequest): void {
    const remarks = prompt('Project manager approval remarks:') || '';
    this.apiService.approveLeaveRequestWithRemarks(leave.id, remarks).subscribe({
      next: () => this.loadLeaveRequests(),
      error: (error) => console.error('Approve Leave Error:', error)
    });
  }

  rejectLeave(leave: LeaveRequest): void {
    const reason = prompt('Rejection reason:') || 'Rejected';
    const remarks = prompt('Project manager remarks (optional):') || '';
    if (!confirm(`Reject leave request from ${leave.employeeName}?`)) return;

    this.apiService.rejectLeaveRequestWithRemarks(leave.id, reason, remarks).subscribe({
      next: () => this.loadLeaveRequests(),
      error: (error) => console.error('Reject Leave Error:', error)
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Approved': return 'approved';
      case 'Rejected': return 'rejected';
      default: return 'pending';
    }
  }

  addLeave(): void {
    if (!this.canCreateLeave) return;

    const type = prompt('Leave type (Annual, Sick, Personal/Casual, Maternity, Paternity, Other):') || 'Personal';
    const startDate = prompt('Start date (YYYY-MM-DD):') || new Date().toISOString().split('T')[0];
    const endDate = prompt('End date (YYYY-MM-DD):') || startDate;
    const reason = prompt('Reason:') || '';
    const half = confirm('Is this a half-day leave?');
    const emergencyName = prompt('Emergency contact name (optional):') || undefined;
    const emergencyPhone = prompt('Emergency contact phone (optional):') || undefined;

    const payload = {
      leaveType: this.normalizeLeaveType(type),
      startDate,
      endDate,
      reason,
      isHalfDay: half,
      halfDayPeriod: half ? 1 : 0,
      emergencyContactName: emergencyName,
      emergencyContactPhone: emergencyPhone,
      submit: true
    };

    this.apiService.createLeaveRequest(payload).subscribe({
      next: () => this.loadLeaveRequests(),
      error: (error) => console.error('Create Leave Error:', error)
    });
  }

  cancelLeave(leave: LeaveRequest): void {
    if (!confirm(`Cancel leave for ${leave.employeeName}?`)) return;
    this.apiService.cancelLeaveRequest(leave.id).subscribe({
      next: () => this.loadLeaveRequests(),
      error: (error) => console.error('Cancel Leave Error:', error)
    });
  }

  private normalizeLeaveStatus(status: unknown): string {
    // LeaveStatus: Draft=0, Pending=1, Approved=2, Rejected=3, Cancelled=4,
    // Submitted=5, TeamLeadReview=6, ProjectManagerReview=7, HrReview=8, CeoReview=9
    if (typeof status === 'number') {
      return [
        'Draft', 'Pending', 'Approved', 'Rejected', 'Cancelled',
        'Submitted', 'TeamLeadReview', 'ProjectManagerReview', 'HrReview', 'CeoReview'
      ][status] ?? 'Pending';
    }
    return String(status ?? 'Pending');
  }

  private normalizeLeaveType(type: string): string {
    const v = (type || '').toLowerCase();
    if (v.includes('annual')) return 'Annual';
    if (v.includes('sick')) return 'Sick';
    if (v.includes('maternity')) return 'Maternity';
    if (v.includes('paternity')) return 'Paternity';
    if (v.includes('casual') || v.includes('personal')) return 'Personal';
    if (v.includes('unpaid')) return 'Unpaid';
    if (v.includes('compensatory')) return 'Compensatory';
    return 'Other';
  }
}